import React from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { CurrencyEnums, DialogEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DialogProps } from "models/propTypes/dialogPropTypes";
//#endregion
//#region components
import QuotationPreview from "./preview";
import { RouteBreadCrumb } from "components/common";
import { useInvoiceService, useQuotesService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function useQuotationPreview() {
  const invoiceService = useInvoiceService();
  const quotesService = useQuotesService();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const getPreviewDialog = (
    quotation?: WintSuperkollAPIModel.Quotation,
    formValues?: InvoiceFormTypes.Create,
    reloadQuotation?: (quotationId: number) => void,
    defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod
  ): DialogProps => {
    let quotationPost: WintSuperkollAPIModel.QuotationPost | undefined = undefined;
    if (formValues) {
      quotationPost = quotesService.convertFormToAPIRequest(formValues);
    } else if (quotation?.quotationState === WintSuperkollAPIModel.QuotationApiState.NotSent) {
      quotationPost = quotation;
    }

    return {
      isOpen: false,
      title: (
        <RouteBreadCrumb
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewInvoice)}
          showHome
        />
      ),
      dialogType: DialogEnums.DialogType.FullScreen,
      children: (
        <QuotationPreview
          quotationPost={quotationPost}
          serialNumber={quotation?.serialNumber}
          status={quotation?.quotationState ?? WintSuperkollAPIModel.QuotationApiState.NotSent}
          customerId={(formValues ? (formValues.customer?.id as number) : quotation?.customerId) ?? 0}
          customerName={(formValues ? formValues.customer?.value : quotation?.customerName) ?? ""}
          referencePersonName={
            formValues ? formValues.referencePerson?.value : quotation?.customerReference?.referenceName
          }
          reloadQuotation={reloadQuotation}
          defaultSendType={defaultSendType}
          total={
            (formValues
              ? (formValues.products ?? [])
                  .map(
                    (p) =>
                      invoiceService.calculateAmount(p.price ?? 0, p.quantity) +
                      invoiceService.calculateTax(p.price ?? 0, p.tax, p.quantity)
                  )
                  .reduce((a, b) => +a + +b, 0)
              : quotation?.totalAmount) ?? 0
          }
          tax={
            (formValues
              ? (formValues.products ?? [])
                  .map((p) => invoiceService.calculateTax(p.price ?? 0, p.tax, p.quantity))
                  .reduce((a, b) => +a + +b, 0)
              : quotation?.totalTax) ?? 0
          }
          currency={(formValues ? formValues.currency : quotation?.currency) ?? CurrencyEnums.CurrencyCode.SEK}
        />
      ),
      maxWidth: "sm",
    };
  };

  const showPreviewDialog = (
    quotation?: WintSuperkollAPIModel.Quotation,
    formValues?: InvoiceFormTypes.Create,
    reloadQuotation?: (quotationId: number) => void,
    defaultSendType?: WintSuperkollAPIModel.InvoiceSendMethod
  ) => {
    dispatch(displayDialog(getPreviewDialog(quotation, formValues, reloadQuotation, defaultSendType)));
  };

  return { getPreviewDialog, showPreviewDialog };
}

export default useQuotationPreview;
